<template>
  <div class="approval-timeline">
    <div
      v-for="(record, index) in recordsList"
      :key="record?.id || index"
      class="timeline-item"
      :class="getRecordStatusClass(record)"
    >
      <div class="timeline-dot">
        <el-icon v-if="getRecordStatus(record) === 'completed'">
          <Check />
        </el-icon>
        <el-icon v-else-if="getRecordStatus(record) === 'processing'">
          <Loading />
        </el-icon>
        <el-icon v-else-if="getRecordStatus(record) === 'rejected'">
          <Close />
        </el-icon>
        <el-icon v-else-if="getRecordStatus(record) === 'cancelled'">
          <Close />
        </el-icon>
        <div v-else class="dot-empty"></div>
      </div>
      <div class="timeline-line" v-if="index < recordsList.length - 1"></div>
      <div class="timeline-content">
        <div class="node-header">
          <span class="node-name">{{ record.currentNode || '审批节点' }}</span>
          <el-tag
            :type="getRecordTagType(record)"
            size="small"
            class="node-status-tag"
          >
            {{ record.statusName || getStatusText(record.approvalStatus) }}
          </el-tag>
        </div>
        <div class="node-info">
          <div class="approver-info" v-if="record.approverName">
            <el-icon><User /></el-icon>
            <span>{{ record.approverName }}</span>
          </div>
          <!-- 只有已通过和已驳回状态才显示审批时间 -->
          <div v-if="shouldShowApprovalTime(record)" class="approval-time">
            <el-icon><Clock /></el-icon>
            <span>{{ record.approvalTime ? formatDateTime(record.approvalTime) : record.createTimeFormatted }}</span>
          </div>
        </div>
        <div v-if="record.rejectReason" class="node-remark">
          <el-icon><ChatDotRound /></el-icon>
          <span>驳回原因：{{ record.rejectReason }}</span>
        </div>
      </div>
    </div>
    <div v-if="recordsList.length === 0" class="empty-nodes">
      <el-empty description="暂无审批记录" :image-size="80" />
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed } from 'vue'
import { Check, Loading, Close, User, Clock, ChatDotRound } from '@element-plus/icons-vue'

const props = defineProps({
  // 审批记录列表（直接使用记录列表显示时间轴）
  records: {
    type: Array,
    default: () => [],
    required: false
  }
})

// 确保 records 始终是数组
const recordsList = computed(() => {
  return Array.isArray(props.records) ? props.records : []
})

// 获取记录状态：pending-待审批, processing-审批中, completed-已完成, rejected-已驳回, waiting-等待中, cancelled-已取消
const getRecordStatus = (record) => {
  const status = String(record.approvalStatus || '').toUpperCase().trim()
  if (status === 'APPROVED') {
    return 'completed'
  } else if (status === 'REJECTED') {
    return 'rejected'
  } else if (status === 'CANCELLED' || status === 'CANCELED') {
    return 'cancelled'
  } else if (status === 'APPROVING' || status === 'PENDING') {
    // 如果有审批时间，说明正在处理
    if (record.approvalTime) {
      return 'processing'
    }
    return 'pending'
  } else if (status === 'WAITING') {
    return 'waiting'
  }
  return 'pending'
}

// 获取记录状态样式类
const getRecordStatusClass = (record) => {
  const status = getRecordStatus(record)
  return {
    'status-pending': status === 'pending',
    'status-processing': status === 'processing',
    'status-completed': status === 'completed',
    'status-rejected': status === 'rejected',
    'status-waiting': status === 'waiting',
    'status-cancelled': status === 'cancelled'
  }
}

// 获取记录标签类型
const getRecordTagType = (record) => {
  const status = getRecordStatus(record)
  const typeMap = {
    'pending': 'info',
    'processing': 'warning',
    'completed': 'success',
    'rejected': 'danger',
    'waiting': 'info',
    'cancelled': 'info'
  }
  return typeMap[status] || 'info'
}

// 获取状态文本
const getStatusText = (status) => {
  const statusUpper = String(status || '').toUpperCase().trim()
  const textMap = {
    'PENDING': '待审批',
    'APPROVING': '审批中',
    'APPROVED': '已通过',
    'REJECTED': '已驳回',
    'WAITING': '等待中',
    'CANCELLED': '已取消',
    'CANCELED': '已取消' // 兼容美式拼写
  }
  return textMap[statusUpper] || '待审批'
}

// 判断是否应该显示审批时间（只有已通过和已驳回状态才显示）
const shouldShowApprovalTime = (record) => {
  const status = String(record.approvalStatus || '').toUpperCase().trim()
  // 只有已通过(APPROVED)和已驳回(REJECTED)状态才显示审批时间
  return (status === 'APPROVED' || status === 'REJECTED') && 
         (record.approvalTime || record.createTimeFormatted)
}

// 格式化日期时间
const formatDateTime = (dateTime) => {
  if (!dateTime) return ''
  const date = new Date(dateTime)
  if (isNaN(date.getTime())) return dateTime
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}`
}
</script>

<style scoped>
.approval-timeline {
  padding: 20px 0;
}

.timeline-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  padding-bottom: 24px;
}

.timeline-item:last-child {
  padding-bottom: 0;
}

.timeline-dot {
  position: relative;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  z-index: 2;
  background: #fff;
  border: 2px solid #e4e7ed;
  transition: all 0.3s;
}

.timeline-item.status-pending .timeline-dot {
  border-color: #c0c4cc;
  background: #f5f7fa;
}

.timeline-item.status-pending .dot-empty {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #c0c4cc;
}

.timeline-item.status-waiting .timeline-dot {
  border-color: #c0c4cc;
  background: #f5f7fa;
}

.timeline-item.status-waiting .dot-empty {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #c0c4cc;
}

.timeline-item.status-cancelled .timeline-dot {
  border-color: #c0c4cc;
  background: #f5f7fa;
  color: #c0c4cc;
}

.timeline-item.status-cancelled .dot-empty {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #c0c4cc;
}

.timeline-item.status-processing .timeline-dot {
  border-color: #e6a23c;
  background: #fdf6ec;
  color: #e6a23c;
}

.timeline-item.status-completed .timeline-dot {
  border-color: #67c23a;
  background: #f0f9ff;
  color: #67c23a;
}

.timeline-item.status-rejected .timeline-dot {
  border-color: #f56c6c;
  background: #fef0f0;
  color: #f56c6c;
}

.timeline-line {
  position: absolute;
  left: 15px;
  top: 32px;
  width: 2px;
  height: calc(100% - 8px);
  background: #e4e7ed;
  z-index: 1;
}

.timeline-item.status-completed .timeline-line {
  background: #67c23a;
}

.timeline-item.status-rejected .timeline-line {
  background: #f56c6c;
}

.timeline-item:last-child .timeline-line {
  display: none;
}

.timeline-content {
  flex: 1;
  margin-left: 16px;
  padding-bottom: 8px;
}

.node-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.node-name {
  font-size: 15px;
  font-weight: 500;
  color: #303133;
}

.node-status-tag {
  margin-left: 12px;
}

.node-info {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 8px;
  font-size: 13px;
  color: #606266;
}

.approver-info,
.approval-time {
  display: flex;
  align-items: center;
  gap: 4px;
}

.approver-info .el-icon,
.approval-time .el-icon {
  font-size: 14px;
}

.node-remark {
  margin-top: 8px;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 13px;
  color: #606266;
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.node-remark .el-icon {
  color: #f56c6c;
  margin-top: 2px;
  flex-shrink: 0;
}

.empty-nodes {
  padding: 40px 0;
  text-align: center;
}
</style>

